/**
 * Shell for the Alerts workspace. The active mode is in the URL (`/alerts`,
 * `/alerts/history`, `/alerts/analytics`) so it survives a reload and can be
 * linked to.
 *
 * Time-bounded modes (History, Analytics, Ingest) read the app-wide range from
 * the top bar; this shell renders no picker of its own. The Queue is not
 * time-bounded at all — suggesting otherwise is precisely what it must never do.
 */
import { Link, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Activity, BarChart3, History, Inbox } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { ALERTS_URL } from "@/alerts/config/alerts-urls";
import { isAlertsMockMode } from "@/alerts/api/mocks/alerts.mocks";
import { useIsStaff } from "@/alerts/api/queries/alerts-monitoring.queries";

const AlertsWorkspaceLayout = () => {
  const { t } = useTranslation("alerts");
  const { pathname } = useLocation();
  const isHistory = pathname.startsWith(ALERTS_URL.HISTORY);
  const isAnalytics = pathname.startsWith(ALERTS_URL.ANALYTICS);
  const isMonitoring = pathname.startsWith(ALERTS_URL.MONITORING);
  const isQueue = !isHistory && !isAnalytics && !isMonitoring;
  // Nav visibility only — the backend authorizes the endpoint itself.
  const isStaff = useIsStaff();

  const tab = (to: string, label: string, Icon: typeof Inbox, active: boolean) => (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </Link>
  );

  return (
    // `overflow-hidden` + min-h-0 all the way down: the list and the detail pane
    // own the scrolling, and the page itself never scrolls horizontally.
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b bg-background px-6">
        <nav className="flex items-center gap-1">
          {tab(ALERTS_URL.QUEUE, t("workspace.tabs.queue"), Inbox, isQueue)}
          {tab(ALERTS_URL.HISTORY, t("workspace.tabs.history"), History, isHistory)}
          {tab(ALERTS_URL.ANALYTICS, t("workspace.tabs.analytics"), BarChart3, isAnalytics)}
          {isStaff && tab(ALERTS_URL.MONITORING, t("workspace.tabs.ingest"), Activity, isMonitoring)}
          {/* Mock mode is invisible otherwise, and a fixture queue looks exactly
              like a real one — an operator (or a tester) must never mistake the
              two. Shown only while mocking; the real API needs no announcement. */}
          {isAlertsMockMode() && (
            <span className="ml-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
              {t("workspace.mockData")}
            </span>
          )}
        </nav>
        {/* No picker here. History, Analytics and Ingest all read the app-wide
            range from the top bar, so a second control in this header was the
            same window rendered twice — two places to look, and two to get out
            of step. The Queue is not time-bounded at all and never had one. */}
      </div>
      <main className="min-h-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default AlertsWorkspaceLayout;
